import {AfterContentInit, AfterViewInit, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {ThemeCardComponent} from '../../../component/card/theme-card/theme-card.component';
import {of, Subscription} from 'rxjs';
import {catchError, skip} from 'rxjs/operators';
import {Product, ProductLine, isThemeFree} from '../../../model/product/product.model';
import {THEMES} from '../../../data/product/product.data';
import {UtilService} from '../../../service/util/util.service';
import {ActivatedRoute, Router} from '@angular/router';
import {URLS} from '../../../data/navigation/navigation.data';
import { MonitorProviderService } from '../../../service/provider/monitor-provider.service';
import {RoutingService} from "../../../service/routing/routing.service";
import { AnalyticsService } from '../../../service/analytics/analytics.service';

import {environmentCommon} from '../../../../environments/environment-common';

@Component({
  selector: 'app-customers-page',
  templateUrl: './themes-page.component.html',
  styleUrls: ['./themes-page.component.scss']
})
export class ThemesPageComponent implements AfterContentInit, AfterViewInit, OnInit, OnDestroy {

  THEMES: ProductLine[] = THEMES;
  URLS: any = URLS;


  THEMES_WALL: Product[] = [];

  sections: any[] = [
    {
      texts: [
        'Themes',
        'Trusted by developers worldwide'
      ]
    },
  ];

  allProducts: Product[] = [];
  // Logical filter result (drives the sidebar count).
  filteredProducts: Product[] = [];
  // What the grid actually renders - briefly holds leaving cards too, so
  // they can play their exit animation before being dropped.
  displayedProducts: Product[] = [];
  // Keys of cards currently animating in (kept hidden until the play step).
  enteringKeys = new Set<string>();

  // --- Expander row state ---
  // The theme whose full-width detail row is open, the displayedProducts
  // index AFTER which the row is inserted (= the last card of the clicked
  // card's row), the open/close animation flag and the caret position (the
  // clicked card's centre, in grid coordinates).
  expandedProduct: Product | null = null;
  expanderAfterIndex = -1;
  expanderOpen = false;
  expanderArrowX = 0;
  private expanderCloseTimer: any;

  // Every rendered theme card, so a filter change can snap their screenshot
  // carousels back to the first slide.
  @ViewChildren(ThemeCardComponent) themeCards?: QueryList<ThemeCardComponent>;

  // --- FLIP animation state ---
  @ViewChild('themesGrid') themesGridRef?: ElementRef<HTMLElement>;
  private flipViewReady = false;
  private flipping = false;
  private flipTimers: any[] = [];
  private flipRaf = 0;

  /** Exposed to the template (themes derive free/premium from per-platform paid). */
  readonly isThemeFree = isThemeFree;

  // Mobile renders the same poster cards, but without the expander row:
  // cards aren't selectable there, so a tap navigates straight to the theme
  // page. Cached + refreshed on resize so the template doesn't call the
  // matchMedia helper on every change-detection tick.
  isMobileCached = false;
  private smallScreenResizeListener?: () => void;

  // Filtering
  filterOptions = {
    appearance: [] as string[],
    category: [] as string[],
    featured: [] as string[],
    platform: [] as any[],
    tag: [] as string[],
    color: [] as string[],
    pricing: [] as string[]
  };

  selectedFilters = {
    appearance: [] as string[],
    category: [] as string[],
    featured: [] as string[],
    platform: [] as string[],
    tag: [] as string[],
    color: [] as string[],
    pricing: [] as string[]
  };

  // Canonical ordering for the Appearance dropdown so it reads Light →
  // Dark → Both regardless of the order the values appeared in the data
  // (default alphabetical sort would give Both → Dark → Light). "Both"
  // is reserved for the All-In-One bundle (the only product that ships
  // both palettes).
  private static readonly APPEARANCE_ORDER = ['Light', 'Dark', 'Both'];

  // The Appearance dropdown shows "Both" for readability, but the
  // underlying data still stores "Light & Dark" for the All-In-One
  // bundle (e.g. the screenshots page and product cards read it
  // verbatim). This map normalises the raw data value into the filter
  // label both when populating `filterOptions.appearance` and when
  // matching `selectedFilters.appearance` against each product.
  private static readonly APPEARANCE_FILTER_LABELS: { [raw: string]: string } = {
    'Light&Dark': 'Both',
  };

  private filterAppearance(raw: string | undefined): string | undefined {
    if (!raw) return raw;
    return ThemesPageComponent.APPEARANCE_FILTER_LABELS[raw] ?? raw;
  }


  // Canonical ordering for the main color buckets - the Color dropdown is
  // built from the intersection of this list and the colors that actually
  // appear in at least one theme's `compactPalette`. "Multicolor" is reserved
  // for the All-In-One bundle (the only product that covers every hue).
  private static readonly MAIN_COLORS = [
    'Red', 'Orange', 'Yellow', 'Green', 'Teal',
    'Blue', 'Purple', 'Pink', 'Brown', 'Beige',
    'Gray', 'Black', 'Multicolor'
  ];

  // Swatch shown next to each option in the Color dropdown / chip. The
  // Multicolor entry is a rainbow gradient because the template binds
  // this string to CSS `background`, which accepts gradients as well as
  // solid colors.
  private static readonly MAIN_COLOR_SWATCHES: { [key: string]: string } = {
    Red:        '#E04848',
    Orange:     '#F08A3C',
    Yellow:     '#F1C232',
    Green:      '#54B257',
    Teal:       '#1FA28E',
    Blue:       '#3D7CC9',
    Purple:     '#8A5BC9',
    Pink:       '#E371A3',
    Brown:      '#8B5A2B',
    Beige:      '#E8DBBC',
    Gray:       '#9A9A9A',
    Black:      '#1A1A1A',
    Multicolor: 'conic-gradient(from 200deg at 50% 50%, #FF3D7F, #FF9F1C, #FFE066, #38B000, #00C2D1, #4361EE, #9D4EDD, #FF3D7F)',
  };

  // Dropdown states
  activeDropdown: string | null = null;

  /** Grid sort order; alphabetical A-Z by default. */
  readonly sortOptions: { value: string, label: string }[] = [
    { value: 'name-asc', label: 'Alphabetical A\u2013Z' },
    { value: 'name-desc', label: 'Alphabetical Z\u2013A' },
    { value: 'newest', label: 'Newest first' },
    { value: 'oldest', label: 'Oldest first' },
  ];
  selectedSort = 'name-asc';
  // True once the user has EXPLICITLY chosen a sort (from the dropdown or via
  // ?sort= in the URL). Until then the default A-Z view stays clean - no
  // ?sort= is written for the initial page / links.
  private sortExplicit = false;

  get selectedSortLabel(): string {
    return this.sortOptions.find(o => o.value === this.selectedSort)?.label || '';
  }

  // Maps a query-string key (lowercase) to the `selectedFilters` group it
  // drives. Kept here so the read/write paths can't drift apart.
  private static readonly QUERY_PARAM_KEYS: { [key: string]: keyof ThemesPageComponent['selectedFilters'] } = {
    appearance: 'appearance',
    category: 'category',
    featured: 'featured',
    platform: 'platform',
    tag: 'tag',
    color: 'color',
    pricing: 'pricing',
  };

  constructor(
    public utilService: UtilService,
    private monitorProviderService: MonitorProviderService,
    private routingService: RoutingService,
    private route: ActivatedRoute,
    private router: Router,
    private analyticsService: AnalyticsService,
  ) {
    window.addEventListener('click', () => {
      this.activeDropdown = null;
    });

    this.THEMES.forEach(productLine => {
      productLine.products.forEach(product => {
        product.category = productLine.name;
        this.allProducts.push(product);
      });
    });

    // No marketplace-stats fetch here: the themes page never displays per-theme
    // downloads or rating (the desktop card shows only the static "Featured"
    // label, the mobile tile shows none), so fetching + animating a count was
    // wasted work and an unnecessary API call.

    this.initializeFilters();
    this.applyFiltersFromQueryParams();
    this.applyFilters();
  }

  /**
   * Seed `selectedFilters` from the URL's query string so deep links like
   * `/themes?appearance=Dark&category=Cities` open with those chips active.
   * Values that don't match a known option are ignored.
   */
  private applyFiltersFromQueryParams( params: { [ key: string ]: any } = this.route.snapshot.queryParams ) {
    if (!params) {
      return;
    }

    const validOptions: { [key: string]: string[] } = {
      appearance: this.filterOptions.appearance,
      category: this.filterOptions.category,
      featured: this.filterOptions.featured,
      platform: this.filterOptions.platform.map((p: any) => p?.name).filter((n: any) => !!n),
      tag: this.filterOptions.tag,
      color: this.filterOptions.color,
      pricing: this.filterOptions.pricing,
    };

    Object.keys(ThemesPageComponent.QUERY_PARAM_KEYS).forEach(paramKey => {
      const filterKey = ThemesPageComponent.QUERY_PARAM_KEYS[paramKey];
      const raw = params[paramKey];
      if (raw === undefined || raw === null || raw === '') {
        return;
      }

      // Angular gives us either a string or an array depending on whether the
      // key appeared once or multiple times in the URL. Normalize both shapes.
      const incoming: string[] = Array.isArray(raw) ? raw : String(raw).split(',');
      const allowed = validOptions[filterKey] || [];

      const matched = incoming
        .map(v => (v || '').trim())
        .filter(v => v.length > 0)
        .map(v => allowed.find(opt => opt.toLowerCase() === v.toLowerCase()))
        .filter((v): v is string => !!v);

      if (matched.length > 0) {
        // The UI is single-select per group, so keep just the first match.
        this.selectedFilters[filterKey] = [matched[0]];
      }
    });

    // Sort is a single value (not a filter group). Honour `?sort=` if it's a
    // known option; otherwise keep the default (A-Z).
    const sortRaw = params['sort'];
    if (sortRaw && this.sortOptions.some(o => o.value === sortRaw)) {
      this.selectedSort = sortRaw;
      this.sortExplicit = true;
    }
  }

  /**
   * Mirror the active filters back into the URL so the current view is
   * shareable and survives a refresh. Replaces history so each click doesn't
   * leak a new entry into the back stack.
   */
  private updateQueryParams() {
    const queryParams: { [key: string]: string | null } = {};
    Object.keys(ThemesPageComponent.QUERY_PARAM_KEYS).forEach(paramKey => {
      const filterKey = ThemesPageComponent.QUERY_PARAM_KEYS[paramKey];
      const values = this.selectedFilters[filterKey];
      // Emit lowercase in the URL; reads are already case-insensitive so the
      // display label (e.g. "JetBrains Marketplace") still renders correctly.
      queryParams[paramKey] = values && values.length > 0 ? values[0].toLowerCase() : null;
    });

    // Sort as a query param, but only once the user has EXPLICITLY chosen one
    // (incl. A-Z). The default/initial view and inbound links stay clean
    // (null key is dropped by the merge).
    queryParams['sort'] = this.sortExplicit ? this.selectedSort : null;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /**
   * A link to this page while it is open (the Platform Coverage table's platform headers, owner
   * 2026-10-17) only changes the query string: the filters in it are applied, the grid refreshed and
   * brought into view. The page's own filter clicks write the same filters back, so they change nothing.
   */
  private onQueryParamsChange( params: { [ key: string ]: any } ): void {
    const before = JSON.stringify( this.selectedFilters );
    const sortBefore = this.selectedSort;
    ( Object.keys( this.selectedFilters ) as ( keyof typeof this.selectedFilters )[] )
      .forEach( key => this.selectedFilters[ key ] = [] );
    this.applyFiltersFromQueryParams( params );
    if ( JSON.stringify( this.selectedFilters ) === before && this.selectedSort === sortBefore ) {
      return;
    }
    this.applyFilters();
    this.scrollToTop();
  }

  ngOnInit(): void {
    this.queryParamsSub = this.route.queryParams.pipe( skip( 1 ) ).subscribe( params => this.onQueryParamsChange( params ) );
    this.isMobileCached = this.utilService.isSmallScreen();
    this.smallScreenResizeListener = () => {
      this.isMobileCached = this.utilService.isSmallScreen();
      // The grid reflows on resize: the expander must follow its card to the
      // card's (possibly new) row. On mobile the tiles navigate instead of
      // expanding, so the row just closes.
      if ( this.expandedProduct ) {
        if ( this.isMobileCached ) {
          this.closeExpander( true );
        } else {
          this.reanchorExpander();
        }
      }
    };
    window.addEventListener( 'resize', this.smallScreenResizeListener, { passive: true } );
  }

  /**
   * Card click on the themes grid: open the full-width detail row under the
   * clicked card's row, or close it when the same card is clicked again.
   */
  toggleExpander( product: Product, index: number ) {
    if ( this.expandedProduct?.key === product.key ) {
      this.closeExpander();
      return;
    }

    // Record which theme a visitor opened on the themes grid (open only, not
    // the close above) so the dashboard ranks the most-expanded themes.
    this.analyticsService.logEvent( 'Theme Expanded', {
      theme: product.name,
      key: product.key,
    } );

    if ( this.expanderCloseTimer ) {
      clearTimeout( this.expanderCloseTimer );
      this.expanderCloseTimer = undefined;
    }

    const wasOpen = !!this.expandedProduct;
    this.expandedProduct = product;
    this.anchorExpander( index );

    if ( wasOpen ) {
      // Moving between cards: the row relocates instantly, only the content
      // swaps - re-running the height animation would make the grid pump.
      this.expanderOpen = true;
      this.scrollExpanderIntoView();
      return;
    }

    // Fresh open: let the row render collapsed once, then play the height
    // transition (grid-template-rows 0fr -> 1fr). rAF waits for that first
    // paint; the timeout is a fallback for contexts where rAF is throttled
    // (hidden/background tabs) so the row never stays stuck closed.
    this.expanderOpen = false;
    let opened = false;
    const open = () => {
      if ( opened ) {
        return;
      }
      opened = true;
      this.expanderOpen = true;
      this.scrollExpanderIntoView();
    };
    requestAnimationFrame( open );
    setTimeout( open, 80 );
  }

  /** Close the detail row - animated by default, instant for layout changes. */
  closeExpander( instant = false ) {
    if ( !this.expandedProduct ) {
      return;
    }
    if ( this.expanderCloseTimer ) {
      clearTimeout( this.expanderCloseTimer );
      this.expanderCloseTimer = undefined;
    }
    this.expanderOpen = false;
    if ( instant || this.prefersReducedMotion() ) {
      this.expandedProduct = null;
      this.expanderAfterIndex = -1;
      return;
    }
    // Keep the row mounted while the collapse transition (550ms) plays, with
    // a small margin so the row is fully collapsed before it unmounts.
    this.expanderCloseTimer = setTimeout( () => {
      this.expandedProduct = null;
      this.expanderAfterIndex = -1;
      this.expanderCloseTimer = undefined;
    }, 600 );
  }

  /**
   * Anchor the detail row after the LAST card of the clicked card's row, so
   * the grid (where the row spans all columns) renders it between that row
   * and the next one. Rows are detected by offsetTop - robust against the
   * auto-fill column count and an already-open expander elsewhere.
   */
  private anchorExpander( index: number ) {
    const grid = this.themesGridRef?.nativeElement;
    if ( !grid ) {
      this.expanderAfterIndex = index;
      return;
    }
    const cards = ( Array.from( grid.children ) as HTMLElement[] )
      .filter( el => el.tagName === 'APP-THEME-CARD' );
    const clicked = cards[ index ];
    if ( !clicked ) {
      this.expanderAfterIndex = index;
      return;
    }

    const rowTop = clicked.offsetTop;
    let last = index;
    while ( last + 1 < cards.length && cards[ last + 1 ].offsetTop === rowTop ) {
      last++;
    }
    // Clamp to the logical list: mid-FLIP the grid briefly holds leaving
    // cards too, and an anchor pointing past the settled list would leave
    // the row unrendered.
    this.expanderAfterIndex = Math.min( last, this.displayedProducts.length - 1 );
    // Caret position relative to the expander row. The row spans the column
    // tracks, which sit centred inside the grid box - so measure from where
    // column 1 starts (= the first card's left edge), not from the grid box.
    const trackStart = cards[ 0 ].offsetLeft;
    this.expanderArrowX = clicked.offsetLeft - trackStart + clicked.offsetWidth / 2;
  }

  /** Re-run the row maths for the currently expanded card (e.g. on resize). */
  private reanchorExpander() {
    if ( !this.expandedProduct ) {
      return;
    }
    const index = this.displayedProducts.findIndex( p => p.key === this.expandedProduct!.key );
    if ( index === -1 ) {
      this.closeExpander( true );
      return;
    }
    // Wait a beat so the measurement sees the post-reflow grid (setTimeout
    // rather than rAF: it also runs in throttled/background tabs).
    setTimeout( () => this.anchorExpander( index ), 60 );
  }

  /** Bring the freshly opened row into view once it has its height. */
  private scrollExpanderIntoView() {
    setTimeout( () => {
      const row = this.themesGridRef?.nativeElement.querySelector( '.expander-row' );
      row?.scrollIntoView( { behavior: 'smooth', block: 'nearest' } );
    }, 570 );
  }

  ngAfterViewInit(): void {
    // The grid is in the DOM now - filter changes can run the FLIP animation.
    this.flipViewReady = true;
  }

  initializeFilters() {
    const categories = new Set<string>();
    const appearances = new Set<string>();
    const tags = new Set<string>();
    const featured = new Set<string>();

    this.allProducts.forEach(p => {
      if (p.category) categories.add(p.category);
      // Store the filter label (e.g. "Both") rather than the raw data
      // value (e.g. "Light & Dark") so the dropdown stays concise. The
      // raw value remains untouched on the product itself.
      const appearance = this.filterAppearance(p.appearance);
      if (appearance) appearances.add(appearance);
      if (p.tags) p.tags.forEach(t => tags.add(t));
      if (p.editorialLabel && p.editorialLabel.trim()) featured.add(p.editorialLabel.trim());
    });

    // Sort by the canonical Light → Dark → Both order; any unknown value
    // falls through to the end alphabetically.
    this.filterOptions.appearance = Array.from(appearances).sort((a, b) => {
      const ia = ThemesPageComponent.APPEARANCE_ORDER.indexOf(a);
      const ib = ThemesPageComponent.APPEARANCE_ORDER.indexOf(b);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return a.localeCompare(b);
    });
    // Alphabetical, but "Collections" (the bundle line) always sorts to the
    // bottom of the category filter.
    this.filterOptions.category = Array.from(categories).sort((a, b) => {
      if (a === 'Collections') { return 1; }
      if (b === 'Collections') { return -1; }
      return a.localeCompare(b);
    });
    this.filterOptions.featured = Array.from(featured).sort();
    // Always offer the full canonical color list - the dropdown stays
    // stable regardless of which themes happen to be present.
    this.filterOptions.color = [...ThemesPageComponent.MAIN_COLORS];
    // Same order as the home-page Platform Coverage matrix
    // (home-page COVERAGE_PLATFORM_KEYS): IDE → Text Editor → Terminal → CAD →
    // Browser → Communication.
    this.filterOptions.platform = [
      environmentCommon.website?.jetbrainsMarketplace,
      environmentCommon.website?.xcode,
      environmentCommon.website?.vscode,
      environmentCommon.website?.openVsx,
      environmentCommon.website?.zed,
      environmentCommon.website?.sublimeText,
      environmentCommon.website?.macos,
      environmentCommon.website?.iterm2,
      environmentCommon.website?.ghostty,
      environmentCommon.website?.claudeCode,
      environmentCommon.website?.codex,
      environmentCommon.website?.opencode,
      environmentCommon.website?.rhino,
      environmentCommon.website?.chrome,
      environmentCommon.website?.firefox,
      environmentCommon.website?.slack,
      environmentCommon.website?.line,
    ].filter(p => p !== undefined).map(p => {
      if (p?.supportedPlatforms && p.supportedPlatforms.length > 0) {
        return p.supportedPlatforms.map(sp => ({ name: sp.name, logo: sp.logo }));
      }
      return [{ name: p?.name, logo: p?.logo }];
    }).reduce((acc, val) => acc.concat(val), []);
    this.filterOptions.tag = Array.from(tags).sort();
    this.filterOptions.pricing = ['Free', 'Paid'];
  }

  toggleFilter(type: string, value: string) {
    if (type === 'tag' || type === 'category' || type === 'appearance' || type === 'featured' || type === 'platform' || type === 'color' || type === 'pricing') {
      const index = this.selectedFilters[type].indexOf(value);
      if (index >= 0) {
        this.selectedFilters[type] = [];
      } else {
        this.selectedFilters[type] = [value];
      }
    } else {
      const index = this.selectedFilters[type].indexOf(value);
      if (index >= 0) {
        this.selectedFilters[type].splice(index, 1);
      } else {
        this.selectedFilters[type].push(value);
      }
    }
    this.analyticsService.logEvent( 'Themes Filtered', {
      type,
      value,
      active: this.selectedFilters[type].indexOf(value) >= 0,
    } );
    this.applyFilters();
    this.updateQueryParams();
    this.scrollToTop();
  }

  toggleDropdown(name: string, event: Event) {
    event.stopPropagation();
    this.activeDropdown = this.activeDropdown === name ? null : name;
  }

  /** Pick a value from an opened dropdown, then close the dropdown. */
  selectFromDropdown(type: string, value: string) {
    this.toggleFilter(type, value);
    this.activeDropdown = null;
  }

  /** Look up the platform logo URL by platform name (for selected chips). */
  getPlatformLogo(name: string): string | null {
    const found = this.filterOptions.platform.find(p => p?.name === name);
    return found?.logo ?? null;
  }

  /** Total number of filter values currently selected across every group. */
  get totalActiveFilters(): number {
    return this.selectedFilters.appearance.length
      + this.selectedFilters.category.length
      + this.selectedFilters.featured.length
      + this.selectedFilters.platform.length
      + this.selectedFilters.tag.length
      + this.selectedFilters.color.length
      + this.selectedFilters.pricing.length;
  }

  /** Reset every filter group and refresh the visible theme list. */
  clearAllFilters() {
    this.selectedFilters = {
      appearance: [],
      category: [],
      featured: [],
      platform: [],
      tag: [],
      color: [],
      pricing: []
    };
    this.analyticsService.logEvent( 'Themes Filters Cleared', {} );
    this.applyFilters();
    this.updateQueryParams();
    this.scrollToTop();
  }

  /** On a filter change, snap each card's screenshot carousel back to slide 0
   *  and resume cycling - but ONLY when no platform is selected. When a platform
   *  IS selected the cards stay frozen on that platform's screenshot (driven by
   *  the [selectedPlatform] input), so they are left alone here. */
  private resetCardScreenshots() {
    // A platform OR pricing filter pins each card to a specific screenshot
    // (via [selectedPlatform] / [selectedPricing]); leave those alone.
    if (this.selectedFilters.platform.length > 0 || this.selectedFilters.pricing.length > 0) {
      return;
    }
    this.themeCards?.forEach(card => card.resetScreenshot());
  }

  /** Scroll the page back to the top after a filter change. */
  private scrollToTop() {
    if (typeof document === 'undefined') {
      return;
    }
    const element = document.getElementById('head');
    if (element) {
      element.scrollIntoView({behavior: 'smooth', block: 'start'});
    } else if (typeof window !== 'undefined') {
      window.scrollTo({top: 0, behavior: 'smooth'});
    }
  }

  getLabelForValue(options: any[], value: string) {
    return options.find(opt => opt.value === value)?.label || value;
  }

  applyFilters() {
    // The detail row is anchored to a specific grid row; a filter change
    // reshuffles the rows, so close it before measuring the FLIP.
    this.closeExpander( true );

    // A filter was added/removed - snap every currently rendered card's
    // screenshot carousel back to the first slide. Cards that stay visible are
    // reused (trackBy key) and keep their slide otherwise; brand-new cards
    // start at slide 0 anyway.
    this.resetCardScreenshots();

    const next = this.computeFilteredProducts();
    this.filteredProducts = next;

    const canAnimate = this.flipViewReady
      && !!this.themesGridRef
      && !this.prefersReducedMotion();

    // No animation possible (first paint / reduced-motion) or one is already
    // running (rapid clicks) - just settle to the new list instantly.
    if (!canAnimate || this.flipping) {
      this.clearPendingFlip();
      this.clearFlipStyles();
      this.enteringKeys.clear();
      this.flipping = false;
      this.displayedProducts = next;
      return;
    }

    this.runFlip(next);
  }

  /** Picks the selected sort and re-runs the grid with the FLIP animation. */
  setSort(value: string) {
    this.activeDropdown = null;
    // Any pick from the dropdown is explicit - even re-picking the current
    // value writes ?sort= (so A-Z becomes visible in the URL on demand).
    this.sortExplicit = true;
    if (this.selectedSort !== value) {
      this.selectedSort = value;
      this.applyFilters();
    }
    this.analyticsService.logEvent( 'Themes Sorted', { sort: value } );
    this.updateQueryParams();
  }

  /** Orders a product list by the selected sort option. */
  private sortProducts(list: Product[]): Product[] {
    const byName = (a: Product, b: Product) => (a.name || '').localeCompare(b.name || '');
    // Real release dates (ISO yyyy-mm-dd from product.data.ts releaseDate);
    // lexicographic compare == chronological.
    const byDate = (a: Product, b: Product) => (a.releaseDate || '').localeCompare(b.releaseDate || '');
    switch (this.selectedSort) {
      case 'name-desc':
        return [...list].sort((a, b) => byName(b, a));
      case 'newest':
        return [...list].sort((a, b) => byDate(b, a));
      case 'oldest':
        return [...list].sort((a, b) => byDate(a, b));
      default:
        return [...list].sort(byName);
    }
  }

  /** Builds the filtered theme list from the currently selected filters,
   * ordered by the selected sort option (alphabetical by default). */
  private computeFilteredProducts(): Product[] {
    return this.sortProducts(this.allProducts.filter(p => {
      const matchCategory = this.selectedFilters.category.length === 0 || this.selectedFilters.category.includes(p.category);
      // Match against the filter label (so "Both" hits products stored
       // as "Light & Dark") rather than the raw appearance value.
      const productAppearanceLabel = this.filterAppearance(p.appearance);
      const matchAppearance = this.selectedFilters.appearance.length === 0
        || (productAppearanceLabel !== undefined && this.selectedFilters.appearance.includes(productAppearanceLabel));
      const matchTag = this.selectedFilters.tag.length === 0 || this.selectedFilters.tag.some(t => p.tags?.includes(t));

      const featuredLabel = p.editorialLabel?.trim() || '';
      const matchFeatured = this.selectedFilters.featured.length === 0
        || (!!featuredLabel && this.selectedFilters.featured.includes(featuredLabel));

      // Colors that the product reads as. The All-In-One bundle is the
      // only product that carries 'Multicolor', so it only appears when
      // that bucket is selected (or when no Color filter is active).
      const productColors = this.getProductMainColors(p);
      const matchColor = this.selectedFilters.color.length === 0
        || this.selectedFilters.color.some(c => productColors.has(c));

      // Platform + pricing are evaluated TOGETHER on the same platform listing:
      // a theme survives only when it has at least one listing that matches both
      // the selected platform AND the selected pricing - otherwise the card would
      // show no platforms at all and is dropped from the results. (e.g. Free +
      // JetBrains must hide the All-In-One bundle, whose JetBrains listing is
      // paid even though it has other free platforms.) When neither filter is
      // active every theme passes, so platform-less themes are not affected.
      const matchPlatformPricing = this.matchesPlatformPricing(p);

      return matchCategory && matchAppearance && matchTag && matchFeatured && matchPlatformPricing && matchColor;
    }));
  }

  /**
   * True when the theme has at least one platform LISTING that satisfies both
   * the active platform filter AND the active pricing filter together (so a card
   * would actually show a platform). When neither filter is active every theme
   * passes. Both conditions must hold on the SAME listing - a paid JetBrains
   * listing plus a separate free Chrome listing does NOT match "Free + JetBrains".
   */
  private matchesPlatformPricing(p: Product): boolean {
    const platformActive = this.selectedFilters.platform.length > 0;
    const pricingActive = this.selectedFilters.pricing.length > 0;
    if (!platformActive && !pricingActive) {
      return true;
    }

    return (p.platforms ?? []).some(plat =>
      this.listingMatchesPlatform(plat) && this.listingMatchesPricing(plat));
  }

  /** Whether a single platform listing matches the active platform filter. */
  private listingMatchesPlatform(plat: any): boolean {
    if (this.selectedFilters.platform.length === 0) {
      return true;
    }

    const names: string[] = (plat.info?.supportedPlatforms && plat.info.supportedPlatforms.length > 0)
      ? plat.info.supportedPlatforms.map((sp: any) => sp.name.toLowerCase())
      : (plat.info ? [plat.info.name.toLowerCase()] : []);

    return this.selectedFilters.platform.some(selectedPlat => {
      const lowerSelected = selectedPlat.toLowerCase();
      return names.some(pPlat => {
        if (lowerSelected === 'jetbrains' || lowerSelected === 'jetbrains marketplace') return pPlat.includes('jetbrains');
        if (lowerSelected === 'chrome' || lowerSelected === 'chrome web store') return pPlat.includes('chrome');
        if (lowerSelected === 'firefox' || lowerSelected === 'firefox add-ons') return pPlat.includes('firefox');
        if (lowerSelected === 'slack') return pPlat.includes('slack');
        if (lowerSelected === 'macos' || lowerSelected === 'macos terminal') return pPlat.includes('macos');
        if (lowerSelected === 'ghostty') return pPlat.includes('ghostty');
        if (lowerSelected === 'iterm2') return pPlat.includes('iterm2');
        if (lowerSelected === 'vs code' || lowerSelected === 'vscode') return pPlat.includes('vs code') || pPlat.includes('vscode');
        if (lowerSelected === 'open vsx' || lowerSelected === 'openvsx') return pPlat.includes('open vsx') || pPlat.includes('openvsx');
        // Handle dynamic supported platforms
        return pPlat === lowerSelected;
      });
    });
  }

  /** Whether a single platform listing matches the active pricing filter
   *  (per-platform `paid` flag; Free = a non-paid listing). */
  private listingMatchesPricing(plat: any): boolean {
    if (this.selectedFilters.pricing.length === 0) {
      return true;
    }
    return this.selectedFilters.pricing.some(price => (plat.paid === true) === (price === 'Paid'));
  }

  /**
   * How many of a theme's color swatches count toward "main colors".
   * Only the first two count - those are the background + primary
   * identity colors. Anything later is an accent, not a main color.
   */
  private static readonly MAIN_COLOR_SWATCH_LIMIT = 2;

  /**
   * Return the main color buckets a theme reads as.
   *
   * Prefers the per-theme `mainColors` array on the product itself -
   * that's the source of truth and matches how each theme is marketed.
   *
   * Falls back to bucketing the first two `compactPalette` via HSL for any
   * theme that hasn't set `mainColors`, so newly added themes still get
   * a sensible default until they're explicitly labelled.
   */
  getProductMainColors(product: Product): Set<string> {
    if (product.mainColors?.length) {
      return new Set(product.mainColors);
    }

    const out = new Set<string>();
    const swatches = product.compactPalette?.slice(0, ThemesPageComponent.MAIN_COLOR_SWATCH_LIMIT) ?? [];
    for (let i = 0; i < swatches.length; i++) {
      const main = ThemesPageComponent.hexToMainColor(swatches[i]);
      if (!main) {
        continue;
      }
      out.add(main);
      if (i === 0 && (main === 'Black' || main === 'White' || main === 'Gray')) {
        break;
      }
    }
    return out;
  }

  /** Swatch color shown beside a main-color label in the dropdown / chip. */
  getMainColorSwatch(name: string): string {
    return ThemesPageComponent.MAIN_COLOR_SWATCHES[name] || '#888';
  }

  /**
   * Map a hex color to one of the MAIN_COLORS buckets via HSL.
   *
   * The hue ranges are tuned so the visible swatches on each theme card
   * land in the bucket a person would name them: deep navy reads as Blue,
   * dusty rose reads as Pink, a muted #A7714C reads as Brown rather than
   * Orange, etc. Returns null for malformed hex.
   */
  private static hexToMainColor(hex: string): string | null {
    if (!hex) {
      return null;
    }
    let h = hex.replace('#', '').trim();
    if (h.length === 3) {
      h = h.split('').map(c => c + c).join('');
    }
    if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) {
      return null;
    }

    const r = parseInt(h.slice(0, 2), 16) / 255;
    const g = parseInt(h.slice(2, 4), 16) / 255;
    const b = parseInt(h.slice(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    const d = max - min;

    let s = 0;
    let hue = 0;
    if (d !== 0) {
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: hue = (g - b) / d + (g < b ? 6 : 0); break;
        case g: hue = (b - r) / d + 2; break;
        case b: hue = (r - g) / d + 4; break;
      }
      hue *= 60;
    }

    // Achromatic / near-achromatic - skip the hue wheel.
    if (l > 0.93) {
      return 'White';
    }
    if (l < 0.07) {
      return 'Black';
    }
    if (s < 0.10) {
      if (l > 0.78) {
        return 'White';
      }
      if (l < 0.25) {
        return 'Black';
      }
      return 'Gray';
    }

    // Very dark, low-to-mid saturation reads as Black, not a colored hue.
    // Catches Reynisfjara's #2A3138 (charcoal) and Tokyo's #2A3051
    // (midnight indigo) which technically sit on the blue arc but feel
    // black to the eye.
    if (l < 0.25 && s < 0.45) {
      return 'Black';
    }

    // Very pale, moderate-saturation reads as White - porcelain
    // backgrounds with a faint hue cast. Pale greens are excluded so
    // mint backgrounds like Rio's #D9EFD2 still bucket as Green.
    if (l > 0.85 && s < 0.60 && !(hue >= 80 && hue < 160)) {
      return 'White';
    }

    // Cream / beige / sand tones: warm hue, very light, modest saturation.
    // Catches background tones like #ECE1C9 / #EFEAD0 that aren't bright
    // white but read as beige rather than yellow or orange.
    if (hue >= 25 && hue <= 70 && s < 0.55 && l > 0.78) {
      return 'Beige';
    }

    // Earth tones in the red / orange arc with reduced saturation and
    // lightness read as Brown. Stops at hue 38 so mustard golds in the
    // 38-65 arc can still bucket as Yellow.
    if (hue >= 10 && hue < 38 && s < 0.70 && l < 0.55) {
      return 'Brown';
    }

    if (hue < 12 || hue >= 345) {
      return 'Red';
    }
    if (hue < 38) {
      return 'Orange';
    }
    if (hue < 65) {
      return 'Yellow';
    }
    if (hue < 160) {
      return 'Green';
    }
    if (hue < 195) {
      return 'Teal';
    }
    if (hue < 250) {
      return 'Blue';
    }
    if (hue < 290) {
      return 'Purple';
    }
    return 'Pink';
  }

  /**
   * FLIP transition: cards that still match glide from their old grid slot to
   * the new one, filtered-out cards fade/shrink away, new cards fade in.
   */
  private runFlip(next: Product[]) {
    const grid = this.themesGridRef!.nativeElement;
    this.flipping = true;

    const newKeys = new Set(next.map(p => p.key));
    const oldKeys = new Set(this.displayedProducts.map(p => p.key));

    // First: snapshot the position of every card currently on screen.
    const oldRects = new Map<string, DOMRect>();
    const cards = Array.from(grid.children) as HTMLElement[];
    for (const el of cards) {
      const k = el.getAttribute('data-key');
      if (k) {
        oldRects.set(k, el.getBoundingClientRect());
      }
    }
    const gridRect = grid.getBoundingClientRect();

    const leaving = this.displayedProducts.filter(p => !newKeys.has(p.key));
    const leavingKeys = new Set(leaving.map(p => p.key));
    this.enteringKeys = new Set(next.filter(p => !oldKeys.has(p.key)).map(p => p.key));

    // Pin leaving cards out of flow so the grid reflows under them.
    for (const el of cards) {
      const k = el.getAttribute('data-key');
      if (k && leavingKeys.has(k)) {
        const r = oldRects.get(k)!;
        el.classList.add('flip-leaving');
        const s = el.style;
        s.position = 'absolute';
        s.left = (r.left - gridRect.left) + 'px';
        s.top = (r.top - gridRect.top) + 'px';
        s.width = r.width + 'px';
        s.height = r.height + 'px';
        s.margin = '0';
        s.zIndex = '1';
        s.pointerEvents = 'none';
      }
    }

    // Render the new set, keeping leaving cards mounted for their exit.
    this.displayedProducts = [...next, ...leaving];

    // Next frame (after Angular has patched the DOM, before paint) measure
    // the new layout, invert the movers, then release them.
    this.flipRaf = requestAnimationFrame(
      () => this.playFlip(grid, oldRects, leavingKeys, next)
    );
  }

  private playFlip(grid: HTMLElement, oldRects: Map<string, DOMRect>,
                   leavingKeys: Set<string>, next: Product[]) {
    const cards = Array.from(grid.children) as HTMLElement[];
    const movers: HTMLElement[] = [];
    const entering: HTMLElement[] = [];

    for (const el of cards) {
      const k = el.getAttribute('data-key');
      if (!k) {
        continue;
      }

      if (leavingKeys.has(k)) {
        // Exit: fade + shrink away (these cards were already visible).
        el.style.transition = 'opacity 220ms ease, transform 220ms ease';
        el.style.opacity = '0';
        el.style.transform = 'scale(0.92)';
        continue;
      }

      if (this.enteringKeys.has(k)) {
        // Enter: arm the transition now; opacity is lifted in the play step
        // so the card paints hidden once first (no flash).
        el.style.transition = 'opacity 380ms ease 140ms';
        entering.push(el);
        continue;
      }

      // Staying: invert - jump it back to where it just was.
      const oldR = oldRects.get(k);
      if (!oldR) {
        continue;
      }
      const newR = el.getBoundingClientRect();
      const dx = oldR.left - newR.left;
      const dy = oldR.top - newR.top;
      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) {
        continue;
      }
      el.style.transition = 'none';
      el.style.transform = `translate(${dx}px, ${dy}px)`;
      movers.push(el);
    }

    // Play: next frame, release the inverted transforms (they glide to their
    // new slot) and fade the entering cards in.
    this.flipRaf = requestAnimationFrame(() => {
      for (const el of movers) {
        el.style.transition = 'transform 440ms cubic-bezier(0.22, 1, 0.36, 1)';
        el.style.transform = 'translate(0, 0)';
      }
      for (const el of entering) {
        el.style.opacity = '1';
      }
    });

    this.flipTimers.push(window.setTimeout(() => this.endFlip(next), 620));
  }

  private endFlip(next: Product[]) {
    this.clearPendingFlip();
    this.displayedProducts = next;
    this.enteringKeys.clear();
    this.clearFlipStyles();
    this.flipping = false;
  }

  private clearPendingFlip() {
    this.flipTimers.forEach(t => clearTimeout(t));
    this.flipTimers = [];
    if (this.flipRaf) {
      cancelAnimationFrame(this.flipRaf);
      this.flipRaf = 0;
    }
  }

  private clearFlipStyles() {
    const grid = this.themesGridRef?.nativeElement;
    if (!grid) {
      return;
    }
    for (const el of Array.from(grid.children) as HTMLElement[]) {
      el.classList.remove('flip-leaving');
      ['transition', 'transform', 'opacity', 'position', 'left', 'top',
        'width', 'height', 'margin', 'z-index', 'pointer-events']
        .forEach(prop => el.style.removeProperty(prop));
    }
  }

  private prefersReducedMotion(): boolean {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** Stable identity for *ngFor so the FLIP animation targets the right cards. */
  trackByKey(_index: number, product: Product): string {
    return product.key;
  }

  ngAfterContentInit(): void {
    this.THEMES.forEach( productLine => {
      this.THEMES_WALL.push( ...productLine.products );
    })
    this.THEMES.forEach( productLine => {
      this.THEMES_WALL.push( ...productLine.products );
    })
    this.THEMES.forEach( productLine => {
      this.THEMES_WALL.push( ...productLine.products );
    })
    this.THEMES.forEach( productLine => {
      this.THEMES_WALL.push( ...productLine.products );
    })
    this.THEMES.forEach( productLine => {
      this.THEMES_WALL.push( ...productLine.products );
    })
    this.THEMES.forEach( productLine => {
      this.THEMES_WALL.push( ...productLine.products );
    })
    this.THEMES.forEach( productLine => {
      this.THEMES_WALL.push( ...productLine.products );
    })
    this.THEMES.forEach( productLine => {
      this.THEMES_WALL.push( ...productLine.products );
    })
    this.THEMES.forEach( productLine => {
      this.THEMES_WALL.push( ...productLine.products );
    })
    this.THEMES.forEach( productLine => {
      this.THEMES_WALL.push( ...productLine.products );
    })
    this.THEMES.forEach( productLine => {
      this.THEMES_WALL.push( ...productLine.products );
    })
    this.THEMES.forEach( productLine => {
      this.THEMES_WALL.push( ...productLine.products );
    })
    this.THEMES.forEach( productLine => {
      this.THEMES_WALL.push( ...productLine.products );
    })
    this.THEMES.forEach( productLine => {
      this.THEMES_WALL.push( ...productLine.products );
    })
    this.THEMES.forEach( productLine => {
      this.THEMES_WALL.push( ...productLine.products );
    })
    this.THEMES.forEach( productLine => {
      this.THEMES_WALL.push( ...productLine.products );
    })
  }

  redirect(theme: any) {
    this.routingService.redirect(URLS.theme, {product: ( theme.key || '' ).toLowerCase(), category: ( theme.category || '' ).toLowerCase()});
  }

  sanitize(value: string): string {
    return value ? value.replace(/<[^>]*>/g, '') : '';
  }

  private queryParamsSub?: Subscription;

  ngOnDestroy(): void {
    this.queryParamsSub?.unsubscribe();
    if ( this.smallScreenResizeListener ) {
      window.removeEventListener( 'resize', this.smallScreenResizeListener );
    }
    if ( this.expanderCloseTimer ) {
      clearTimeout( this.expanderCloseTimer );
    }
    this.clearPendingFlip();
  }
}
